import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FiSearch, FiTrash2, FiEdit2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, planFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/users', {
        params: { page: pagination.page, search, plan: planFilter }
      });
      setUsers(data.users);
      setPagination(data.pagination);
    } catch {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch {
      toast.error('Failed to delete user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      await api.put(`/admin/users/${editUser._id}`, {
        plan: editUser.plan,
        role: editUser.role,
        isVerified: editUser.isVerified
      });
      toast.success('User updated');
      setEditUser(null);
      fetchUsers();
    } catch {
      toast.error('Failed to update user');
    }
  };

  return (
    <div>
      <h1 style={styles.title}>User Management</h1>

      <div style={styles.toolbar}>
        <form onSubmit={handleSearch} style={styles.searchForm}>
          <FiSearch style={styles.searchIcon} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email" style={styles.searchInput} />
        </form>
        <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }} style={styles.select}>
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>

      <div style={styles.table}>
        <div style={styles.tableHeader}>
          <span style={{ flex: 2 }}>Name</span>
          <span style={{ flex: 2 }}>Email</span>
          <span style={{ flex: 1 }}>Credits</span>
          <span style={{ flex: 1 }}>Status</span>
          <span style={{ flex: 1 }}>Joined</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Actions</span>
        </div>

        {users.map(u => (
          <div key={u._id} style={styles.tableRow}>
            <span style={{ flex: 2, fontWeight: 500 }}>{u.name}</span>
            <span style={{ flex: 2, color: 'var(--text-muted)', fontSize: 13 }}>{u.email}</span>
            <span style={{ flex: 1 }}>
              <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: (u.credits || 0) > 1000 ? 'var(--accent-dim)' : 'var(--bg-tertiary)', color: (u.credits || 0) > 1000 ? 'var(--accent)' : 'var(--text-muted)' }}>{(u.credits || 0).toLocaleString()}</span>
            </span>
            <span style={{ flex: 1 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 6, background: u.isVerified ? 'var(--success)' : 'var(--warning)' }} />
              <span style={{ fontSize: 13 }}>{u.isVerified ? 'Verified' : 'Pending'}</span>
            </span>
            <span style={{ flex: 1, color: 'var(--text-muted)', fontSize: 13 }}>{new Date(u.createdAt).toLocaleDateString()}</span>
            <span style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
              <button onClick={() => setEditUser({ ...u })} style={styles.actionBtn}><FiEdit2 size={14} /></button>
              <button onClick={() => handleDelete(u._id)} style={{ ...styles.actionBtn, color: 'var(--danger)' }}><FiTrash2 size={14} /></button>
            </span>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div style={styles.pagination}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{pagination.total} total users</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} style={styles.pageBtn}><FiChevronLeft /></button>
          <span style={{ padding: '6px 12px', fontSize: 14, color: 'var(--text-primary)' }}>{pagination.page} / {pagination.pages}</span>
          <button disabled={pagination.page >= pagination.pages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} style={styles.pageBtn}><FiChevronRight /></button>
        </div>
      </div>

      {/* Edit Modal */}
      {editUser && (
        <div style={styles.modalOverlay} onClick={() => setEditUser(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 20 }}>Edit User: {editUser.name}</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Plan</label>
              <select value={editUser.plan} onChange={e => setEditUser({ ...editUser, plan: e.target.value })} style={styles.modalSelect}>
                <option value="free">Free</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>Role</label>
              <select value={editUser.role} onChange={e => setEditUser({ ...editUser, role: e.target.value })} style={styles.modalSelect}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, cursor: 'pointer' }}>
              <input type="checkbox" checked={editUser.isVerified} onChange={e => setEditUser({ ...editUser, isVerified: e.target.checked })} />
              <span>Verified</span>
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditUser(null)} style={styles.cancelBtn}>Cancel</button>
              <button onClick={handleUpdateUser} style={styles.saveBtn}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  title: { fontSize: 28, fontWeight: 700, marginBottom: 24 },
  toolbar: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  searchForm: { flex: 1, position: 'relative', minWidth: 200 },
  searchIcon: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' },
  searchInput: { width: '100%', padding: '10px 10px 10px 36px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none' },
  select: { padding: '10px 14px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none' },
  table: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  tableHeader: { display: 'flex', padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: 0.5 },
  tableRow: { display: 'flex', padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', alignItems: 'center' },
  actionBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 6, borderRadius: 4, display: 'flex' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 },
  pageBtn: { padding: '6px 10px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-primary)', cursor: 'pointer', display: 'flex' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, width: 400, maxWidth: '90vw' },
  label: { display: 'block', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 },
  modalSelect: { width: '100%', padding: '10px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14 },
  cancelBtn: { padding: '8px 16px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', cursor: 'pointer' },
  saveBtn: { padding: '8px 16px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'white', fontWeight: 600, cursor: 'pointer' }
};
